import type { Statistics, StatisticsForCompany, StatisticsForFlow } from '@keyvue/one-api-client';
import type { StatisticDisplayDataDefaults } from '@/flow/statistics/types/StatisticDisplayDataDefaults';
import type StatisticDisplayData from '@/flow/statistics/types/StatisticDisplayData';

const displayDefaults = {
    newFlowsCount: {
        title: 'Nieuw in flow',
        renderType: 'count',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
    activeFlowsCount: {
        title: 'Actief in flow',
        renderType: 'count',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
    successfulFlowsCount: {
        title: 'Voltooide opvolgingen',
        renderType: 'count',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
    sentEmailsCount: {
        title: 'Verzonden e-mails',
        renderType: 'percentage',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
    openedEmailsCount: {
        title: 'Geopende e-mails',
        renderType: 'count',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
    clickedEmailsCount: {
        title: 'Geklikt in e-mail',
        renderType: 'percentage',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
    openedEmailsPercentage: {
        title: 'Geopende e-mails',
        renderType: 'percentage',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
    clickedEmailsPercentage: {
        title: 'Geklikt in e-mail',
        renderType: 'percentage',
        graph: {
            backgroundColor: '#5756A1',
            stroke: '#01b406',
            fill: '#456a80',
        },
    },
} as Record<keyof Statistics, StatisticDisplayDataDefaults>;

function generateGraphData(key: keyof Statistics, defaults: StatisticDisplayDataDefaults, statistics: StatisticsForCompany | StatisticsForFlow) {
    if (!('data' in statistics)) {
        return undefined;
    }

    const data = Object.entries(statistics.data)
        // simplify data
        .map(([label, entry]) => ({
            label,
            value: entry[key],
        }))
        // map key to value
        .reduce((acc, { label, value }) => {
            acc[label] = value;
            return acc;
        }, {}) as Record<string, number>;

    const highestValue = Math.max(...Object.values(data)) / 100;
    const scaledData = Object.entries(data).reduce((acc, [k, value]) => {
        acc[k] = ((value / 100)) / (highestValue);
        return acc;
    }, {} as Record<string, number>);

    // above scaling math fails when highestValue is 0, so explicitly set all values to 0
    if (highestValue === 0) {
        Object.keys(scaledData).forEach((k) => {
            scaledData[k] = 0;
        });
    }

    return {
        ...defaults.graph,
        dataScale: statistics.dataScale,
        data,
        scaledData: Object.values(scaledData),
    };
}

export default function generateDisplayData(statistics: StatisticsForCompany | StatisticsForFlow): StatisticDisplayData[] {
    return Object
        .keys(displayDefaults)
        .map((key) => {
            const defaults = displayDefaults[key as keyof Statistics];

            return {
                title: defaults.title,
                currentValue: statistics.current[key],
                previousValue: statistics.previous?.[key],
                renderType: defaults.renderType,
                graph: generateGraphData(key as keyof Statistics, defaults, statistics),
            } as StatisticDisplayData;
        });
}
